<template>
  <div class="tool-list-container">
    <!-- Empty State - Show when no tools match filters -->
    <EmptyResults 
      v-if="!tools || tools.length === 0"
      title="No Tools Found"
      message="We couldn't find any tools matching your current filters."
      icon="heroicons:filter-solid"
      :filters="filters"
      :useCase="useCase"
    />

    <!-- Tool List - Show when tools exist -->
    <div v-else class="tool-list space-y-3">
      <div
        v-for="(tool, index) in tools"
        :key="tool.id"
        class="bg-gray-800 shadow-lg rounded-lg overflow-visible tool-card relative"
        v-intersect="{ 
          handler: (isIntersecting) => handleIntersection(isIntersecting, index), 
          options: { threshold: 0.1 }
        }"
      >
        <!-- Desktop Layout -->
        <div class="hidden md:grid md:grid-cols-[auto,1fr,auto] md:gap-3 md:items-start px-3 py-3">
          <Link :href="`/tool/${tool.slug}`">
            <div v-if="loadedTools[index]">
              <img
                :src="getToolImageUrl(tool)"
                :alt="`${tool.name} logo`"
                class="w-10 h-10 rounded-lg object-cover hover:opacity-80 transition-opacity"
                loading="lazy"
                @error="onImageError($event)"
              />
            </div>
            <div v-else class="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
              <Icon icon="heroicons:photo" class="w-6 h-6 text-gray-500" />
            </div>
          </Link>
          
          <div>
            <Link 
              :href="`/tool/${tool.slug}`"
              class="text-sm font-semibold text-[16px] text-white hover:text-blue-400 transition-colors truncate block flex hover:underline hover:translate-x-0.5 transition-transform"
            >
              {{ tool.name }}
              <Icon icon="bx:bx-link-external" class="h-3 w-3 ml-2 mt-1" />
            </Link>
            <p class="text-xs text-gray-300 description-three-lines mt-1">
              {{ truncateDescription(tool.short_description, 180) }}
            </p>
            <div v-if="loadedTools[index]" class="mt-2 flex flex-wrap gap-1">
              <span 
                v-for="platform in tool.platforms" 
                :key="platform.id" 
                class="desktop-tag desktop-tag-platform"
              >
                {{ platform.name }}
              </span>
              <span 
                v-if="tool.pricing_model" 
                class="desktop-tag desktop-tag-pricing"
              >
                {{ tool.pricing_model }}
              </span>
              <span
                v-for="tag in tool.tags"
                :key="tag.id"
                class="desktop-tag desktop-tag-tag flex items-center gap-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3 w-3"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2l9 5-9 5-9-5z" />
                </svg>
                {{ tag.name }}
              </span>
              <!-- Features Badge -->
              <span 
                v-if="tool.features && tool.features.length > 0"
                class="desktop-tag desktop-tag-feature cursor-pointer hover:opacity-80 transition-opacity"
                @click="showFeatures(tool)"
                :title="getFeaturesList(tool.features)"
              >
                {{ tool.features.length }} Features
              </span>
            </div>
            <div v-else class="mt-2 flex flex-wrap gap-1">
              <div class="w-16 h-5 bg-gray-700 rounded-md"></div>
              <div class="w-20 h-5 bg-gray-700 rounded-md"></div>
            </div>
          </div>
          
          <div class="flex flex-col space-y-2 items-center">
            <!-- Favorite Button -->
            <FavoriteButtonModal
              :toolId="tool.id"
              :initialFavoritesCount="tool.favorites_count ?? 0"
              :initialIsFavorite="tool.is_favorited"
              :showCount="true"
            />
            
            <!-- Compare Button -->
            <button 
              @click="toggleInComparison(tool)"
              :class="[
                'inline-flex items-center justify-center transition-all rounded-lg',
                'gap-1 px-2 py-1 text-xs',
                isInCompare(tool.id) ? 
                  'bg-purple-600 hover:bg-purple-700 text-white' : 
                  'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
              ]"
              :title="isInCompare(tool.id) ? 'Remove from compare' : 'Add to compare'"
            >
              <Icon 
                icon="heroicons:square-2-stack" 
                class="w-3.5 h-3.5" 
                aria-hidden="true"
              />
              <span>{{ isInCompare(tool.id) ? 'Remove' : 'Compare' }}</span>
            </button>
          </div>
        </div>

        <!-- Mobile Layout -->
        <div class="md:hidden flex flex-col">
          <!-- Top Section: Logo, Name, Actions -->
          <div class="p-3 flex items-start justify-between">
            <!-- Logo and Name -->
            <div class="flex items-start gap-2">
              <Link :href="`/tool/${tool.slug}`" class="shrink-0">
                <div v-if="loadedTools[index]">
                  <img
                    :src="getToolImageUrl(tool)"
                    :alt="`${tool.name} logo`"
                    class="w-10 h-10 rounded-lg object-cover hover:opacity-80 transition-opacity"
                    loading="lazy"
                    @error="onImageError($event)"
                  />
                </div>
                <div v-else class="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                  <Icon icon="heroicons:photo" class="w-6 h-6 text-gray-500" />
                </div>
              </Link>
              
              <div>
                <Link 
                  :href="`/tool/${tool.slug}`"
                  class="text-sm font-semibold text-[16px] text-white hover:text-blue-400 transition-colors truncate block flex hover:underline hover:translate-x-0.5 transition-transform"
                >
                  {{ tool.name }}
                  <Icon icon="bx:bx-link-external" class="h-3 w-3 ml-2 mt-1" />
                </Link>
                <p class="text-xs text-gray-300 mt-1 line-clamp-3">
                  {{ truncateDescription(tool.short_description, 130) }}
                </p>
              </div>
            </div>
            
            <!-- Actions - Vertically aligned -->
            <div class="flex flex-col space-y-2 items-center">
              <FavoriteButtonModal
                :toolId="tool.id"
                :initialFavoritesCount="tool.favorites_count ?? 0"
                :initialIsFavorite="tool.is_favorite || false"
              />
              
              <button 
                @click="toggleInComparison(tool)"
                :class="[
                  'inline-flex items-center justify-center transition-all rounded-lg',
                  'p-1.5',
                  isInCompare(tool.id) ? 
                    'bg-purple-600 hover:bg-purple-700 text-white' : 
                    'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                ]"
                :title="isInCompare(tool.id) ? 'Remove from compare' : 'Add to compare'"
              >
                <Icon 
                  icon="heroicons:square-2-stack" 
                  class="w-3.5 h-3.5" 
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          
          <!-- Bottom Section: Tags row -->
          <div v-if="loadedTools[index]" class="px-3 pb-3 mt-0.5">
            <div class="tags-scroll-wrapper relative">
              <!-- Scrollable tags container -->
              <div class="tags-container" :id="'tags-container-' + tool.id" @scroll="(e) => checkScroll(e.target)">
                <div class="tag-scroll-container">
                  <span 
                    v-for="platform in tool.platforms" 
                    :key="platform.id" 
                    class="tag tag-platform"
                  >
                    {{ platform.name }}
                  </span>
                  <span 
                    v-if="tool.pricing_model" 
                    class="tag tag-pricing"
                  >
                    {{ tool.pricing_model }}
                  </span>
                  <span
                    v-for="tag in tool.tags"
                    :key="tag.id"
                    class="tag tag-tag"
                  >
                    {{ tag.name }}
                  </span>
                  <!-- Features Badge -->
                  <span 
                    v-if="tool.features && tool.features.length > 0"
                    class="tag tag-feature cursor-pointer hover:opacity-80"
                    @click="showFeatures(tool)"
                  >
                    {{ tool.features.length }} Features
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="px-3 pb-3 mt-0.5">
            <div class="flex gap-2">
              <div class="w-16 h-6 bg-gray-700 rounded-full"></div>
              <div class="w-20 h-6 bg-gray-700 rounded-full"></div>
            </div>
          </div>
        </div>
        
        <!-- Loading overlay (shown while image and data loads) -->
        <div 
          v-if="!loadedTools[index]"
          class="absolute inset-0 bg-gray-800/50 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none"
        >
          <div class="loader-dots flex space-x-2">
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
          </div>
        </div>
      </div>
      
      <!-- Load more trigger -->
      <div 
        v-if="hasMoreToolsToLoad"
        ref="loadMoreTrigger" 
        class="h-16 w-full flex justify-center items-center"
      >
        <div v-if="isLoadingMore" class="flex items-center space-x-3">
          <div class="loader-dots flex space-x-2">
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
            <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
          </div>
          <span class="text-gray-400">Loading more tools...</span>
        </div>
      </div>
      
      <!-- Notification for compare action feedback -->
      <transition name="fade">
        <div 
          v-if="showNotification" 
          class="fixed bottom-16 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg z-50"
        >
          {{ notificationText }}
        </div>
      </transition>
    
      <!-- Features Modal -->
      <div 
        v-if="showFeaturesModal" 
        class="fixed inset-0 flex items-center justify-center z-50"
        @click.self="showFeaturesModal = false"
      >
        <div class="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-semibold text-white">{{ currentTool?.name }} Features</h3>
            <button @click="showFeaturesModal = false" class="text-gray-400 hover:text-white">
              <Icon icon="heroicons:x-mark" class="w-6 h-6" />
            </button>
          </div>
          
          <ul class="space-y-2 max-h-60 overflow-y-auto pr-2">
            <li 
              v-for="feature in currentTool?.features" 
              :key="feature.id"
              class="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-700/50"
            >
              <div class="text-green-400 mt-0.5" v-if="feature.pivot && feature.pivot.has_feature">
                <Icon icon="heroicons:check" class="w-5 h-5" />
              </div>
              <div class="text-red-400 mt-0.5" v-else-if="feature.pivot && feature.pivot.has_feature === false">
                <Icon icon="heroicons:x-mark" class="w-5 h-5" />
              </div>
              <div class="text-gray-400 mt-0.5" v-else>
                <Icon icon="heroicons:question-mark-circle" class="w-5 h-5" />
              </div>
              
              <div>
                <span class="text-white">{{ feature.name }}</span>
                <p v-if="feature.pivot && feature.pivot.details" class="text-sm text-gray-400 mt-1">
                  {{ feature.pivot.details }}
                </p>
              </div>
            </li>
          </ul>
          
          <Link 
            :href="`/tool/${currentTool?.slug}`"
            class="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-center block"
          >
            See Full Details
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, nextTick, onUnmounted, watch } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import FavoriteButtonModal from '@/Components/FavoriteButtonModal.vue';
import { Icon } from '@iconify/vue';
import EmptyResults from './EmptyResults.vue';
import { useCompareStore } from '@/store/compareModule';
import axios from 'axios';

const props = defineProps({
  tools: {
    type: Array,
    required: true,
  },
  startIndex: {
    type: Number,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  useCase: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['loadMore']);

// Get auth from usePage
const { auth } = usePage().props;
const favoriteToolIds = ref([]);

// Notification state
const showNotification = ref(false);
const notificationText = ref('');

// Features modal state
const showFeaturesModal = ref(false);
const currentTool = ref(null);

// Scrolling state
const hasScrollableContent = ref(false);

// Lazy loading state
const loadedTools = ref({});
const isLoadingMore = ref(false);
const loadMoreTrigger = ref(null);
const hasMoreToolsToLoad = ref(false);

// Compare store
const compareStore = useCompareStore();

// Observer references for cleanup
let scrollObserver = null;
let keyboardListener = null;

// Function to check if a tool is favorited
const isFavorited = (toolId) => {
  // If user is not logged in or favorites aren't loaded, always return false
  if (!auth.user || favoriteToolIds.value.length === 0) {
    return false;
  }
  
  return favoriteToolIds.value.includes(toolId);
};

// Fetch user's favorite tool IDs on component mount
const fetchFavorites = async () => {
  if (auth.user) {
    try {
      const response = await axios.get('/api/favorites/ids');
      favoriteToolIds.value = response.data;
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  }
};

// Custom directive for intersection observer
const vIntersect = {
  mounted(el, binding) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          binding.value.handler(true);
          if (binding.value.once) {
            observer.unobserve(el);
          }
        } else {
          binding.value.handler(false);
        }
      });
    }, binding.value.options || { threshold: 0.1 });
    
    observer.observe(el);
    el._observer = observer;
  },
  unmounted(el) {
    if (el._observer) {
      el._observer.disconnect();
    }
  }
};

// Handle intersection events
const handleIntersection = (isIntersecting, index) => {
  if (isIntersecting && !loadedTools.value[index]) {
    // Add a small delay to prevent too many operations at once
    setTimeout(() => {
      loadedTools.value[index] = true;
    }, index * 50); // Stagger loading for smoother experience
  }
};

// Check if tags container is scrollable
const checkScroll = (container) => {
  if (!container) return;
  
  const isScrollable = container.scrollWidth > container.clientWidth + 5;
  hasScrollableContent.value = isScrollable;
  
  // Update the parent container's padding if scrollable
  const wrapper = container.closest('.tags-scroll-wrapper');
  if (wrapper) {
    wrapper.style.paddingLeft = isScrollable ? '1rem' : '0';
    wrapper.style.paddingRight = isScrollable ? '1rem' : '0';
  }
};

// Get tool image with fallback
const getToolImageUrl = (tool) => {
  if (!tool || !tool.media) return '/storage/no-logo.png';
  return tool.media[0]?.original_url || '/storage/no-logo.png';
};

// Image error handler
const onImageError = (event) => {
  event.target.src = '/storage/no-logo.png';
};

// Truncate description to fit UI
const truncateDescription = (description, maxLength) => {
  if (!description) return '';
  
  if (description.length <= maxLength) {
    return description;
  }
  
  // Break at a space to avoid cutting words
  let truncated = description.substring(0, maxLength);
  let lastSpaceIndex = truncated.lastIndexOf(' ');
  
  // Only use if space is not too far back
  if (lastSpaceIndex > maxLength * 0.8) {
    return description.substring(0, lastSpaceIndex) + '...';
  }
  
  return truncated + '...';
};

// Check if a tool is in comparison
const isInCompare = (toolId) => {
  return compareStore.isInCompare(toolId);
};

// Toggle a tool in comparison
const toggleInComparison = (tool) => {
  if (isInCompare(tool.id)) {
    // Remove from comparison
    compareStore.removeTool(tool.id);
    showFeedback('Removed from comparison');
  } else {
    // Add to comparison
    if (!compareStore.isCompareFull) {
      const added = compareStore.addTool(tool);
      if (added) {
        showFeedback('Added to comparison');
      }
    } else {
      showFeedback(`Maximum of ${compareStore.maxTools} tools reached`);
    }
  }
};

// Show notification feedback
const showFeedback = (message) => {
  notificationText.value = message;
  showNotification.value = true;
  
  setTimeout(() => {
    showNotification.value = false;
  }, 2000);
};

// Show features modal
const showFeatures = (tool) => {
  currentTool.value = tool;
  showFeaturesModal.value = true;
};

// Handle keydown events for modal
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && showFeaturesModal.value) {
    showFeaturesModal.value = false;
  }
};

// Get features list for tooltip
const getFeaturesList = (features) => {
  if (!features || features.length === 0) return '';
  
  return features
    .slice(0, 5)
    .map(f => f.name)
    .join(', ') + 
    (features.length > 5 ? ` and ${features.length - 5} more...` : '');
};

// Setup infinite scroll functionality
onMounted(async () => {
  // Fetch favorites first
  await fetchFavorites();
  
  // Setup intersection observer for infinite scrolling
  if (loadMoreTrigger.value) {
    scrollObserver = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoadingMore.value) {
        emit('loadMore');
        isLoadingMore.value = true;
        
        // Reset loading state after a delay
        setTimeout(() => {
          isLoadingMore.value = false;
        }, 1000);
      }
    }, { rootMargin: '200px' });
    
    scrollObserver.observe(loadMoreTrigger.value);
  }
  
  // Setup loading for initially visible tools (first batch)
  nextTick(() => {
    // Load first 5 tools immediately
    for (let i = 0; i < Math.min(5, props.tools.length); i++) {
      loadedTools.value[i] = true;
    }
  });
  
  // Add keyboard event listener for modal
  document.addEventListener('keydown', handleKeyDown);
  keyboardListener = handleKeyDown;
});

// Properly set up cleanup outside of onMounted
onUnmounted(() => {
  // Clean up the scroll observer
  if (scrollObserver) {
    scrollObserver.disconnect();
  }
  
  // Remove keyboard event listener
  if (keyboardListener) {
    document.removeEventListener('keydown', keyboardListener);
  }
});

// Update hasMoreToolsToLoad when props change
watch(() => props.tools.length, (newLength) => {
  hasMoreToolsToLoad.value = newLength > 10; // Only show "load more" if we have more than 10 tools
}, { immediate: true });
</script>


<style scoped>
.tool-list-container {
  width: 100%;
}

/* Updated to 3 lines for desktop */
.description-three-lines {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 3.6em; /* Minimum height to prevent layout shift */
  line-height: 1.2;
  text-overflow: ellipsis;
  word-break: break-word;
}

/* Mobile line clamp */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 3.6em; /* Minimum height to prevent layout shift */
  line-height: 1.2;
  text-overflow: ellipsis;
  word-break: break-word;
}

/* Desktop tags styling */
.desktop-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
  color: white;
}

.desktop-tag-platform {
  background-color: rgba(66, 153, 225, 0.3); /* Soft blue */
  color: rgba(191, 219, 254, 1); /* Light blue text */
}

.desktop-tag-pricing {
  background-color: rgba(72, 187, 120, 0.2); /* Soft green */
  color: rgba(167, 243, 208, 1); /* Light green text */
}

.desktop-tag-tag {
  background-color: rgba(113, 128, 150, 0.3); /* Soft gray */
  color: rgba(203, 213, 225, 1); /* Light gray text */
}

.desktop-tag-feature {
  background-color: rgba(124, 58, 237, 0.2); /* Soft purple */
  color: rgba(216, 180, 254, 1); /* Light purple text */
}

/* Mobile tags styling */
.tags-scroll-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.tags-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  scroll-behavior: smooth;
  padding: 0.25rem 0;
}

.tags-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

.tag-scroll-container {
  display: flex;
  gap: 0.5rem;
  padding-bottom: 0.25rem;
  min-width: min-content;
}

.tag {
  display: flex;
  align-items: center;
  white-space: nowrap;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
  color: white;
  flex-shrink: 0;
}

/* Softer color palette for tags */
.tag-platform {
  background-color: rgba(66, 153, 225, 0.3); /* Soft blue */
  color: rgba(191, 219, 254, 1); /* Light blue text */
  border: 1px solid rgba(66, 153, 225, 0.2);
}

.tag-pricing {
  background-color: rgba(72, 187, 120, 0.2); /* Soft green */
  color: rgba(167, 243, 208, 1); /* Light green text */
  border: 1px solid rgba(72, 187, 120, 0.2);
}

.tag-tag {
  background-color: rgba(113, 128, 150, 0.3); /* Soft gray */
  color: rgba(203, 213, 225, 1); /* Light gray text */
  border: 1px solid rgba(113, 128, 150, 0.2);
}

.tag-feature {
  background-color: rgba(124, 58, 237, 0.2); /* Soft purple */
  color: rgba(216, 180, 254, 1); /* Light purple text */
  border: 1px solid rgba(124, 58, 237, 0.2);
}

/* Scroll indicators */
.scroll-indicator {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background-color: rgba(31, 41, 55, 0.9);
  color: white;
  border-radius: 9999px;
  z-index: 10;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.scroll-indicator:hover {
  opacity: 1;
  transform: scale(1.1);
}

.scroll-indicator-left {
  left: -0.5rem;
}

.scroll-indicator-right {
  right: -0.5rem;
}

/* Animation styling */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

/* Scrollbar styling for features modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(75, 85, 99, 0.8);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(107, 114, 128, 0.8);
}

/* Tool card styling */
.tool-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tool-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Loading animation */
@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.3;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Loading dots animation */
@keyframes loader-pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

.loader-dots div {
  animation: loader-pulse 1.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>